<script lang="ts">
    import {pipe} from 'core/utils';
    import type {ProvidedSeeking, Seeking} from 'typings/portaro.be.types';
    import DesiredExemplar from '../../../user/DesiredExemplar.svelte';
    import {loc} from 'shared/utils/pipes';
    import {getDateFormatter, getInjector, getLocalization} from 'core/svelte-context/context';
    import KpPrice from 'shared/components/kp-price/KpPrice.svelte';
    import KpCollapsibleMenuWrapper from 'shared/ui-widgets/menu-wrapper/KpCollapsibleMenuWrapper.svelte';
    import {Kind} from 'shared/constants/portaro.constants';
    import Label from 'shared/components/label/Label.svelte';
    import {findFirst, byId} from 'shared/utils/array-utils';
    import {SeekingIntentId} from '../../../ill/seeking-constants';
    import SeekingService from '../../../ill/seeking.service';
    import type {SearchManager} from '../../search-manager/search-manager';

    export let seeking: ProvidedSeeking;
    export let refreshSearchManager: SearchManager<ProvidedSeeking>;

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();
    const seekingService = getInjector().getByToken<SeekingService>(SeekingService.serviceName);
    let processing = false;

    function refreshAll() {
        refreshSearchManager.refreshSearch();
    }

    async function refreshSingle() {
        seeking = await seekingService.getById<ProvidedSeeking>(seeking.id);
    }

    function startProcessing() {
        processing = true;
    }

    function endProcessing() {
        processing = false;
    }

    async function doSomething(fn: () => Promise<any>, refreshWhole: boolean = false) {
        startProcessing();
        try {
            await fn();
            if (refreshWhole) {
                refreshAll();
            } else {
                await refreshSingle();
            }
        } finally {
            endProcessing();
        }
    }

    async function acceptIllSeekingActiveProvision() {
        await doSomething(() => seekingService.acceptSeekingActiveProvision(seeking));
    }

    async function conditionallyAcceptIllSeekingActiveProvision() {
        await doSomething(() => seekingService.conditionallyAcceptSeekingActiveProvision(seeking));
    }

    async function sendIllSeekingActiveProvisionExemplar() {
        await doSomething(() => seekingService.sendIllSeekingActiveProvisionExemplar(seeking));
    }

    async function receiveBackIllSeekingActiveProvisionExemplar() {
        await doSomething(() => seekingService.receiveBackIllSeekingActiveProvisionExemplar(seeking));
    }

    async function editProvidedSeekingActiveProvisionData() {
        await doSomething(() => seekingService.editProvidedSeekingActiveProvisionData(seeking));
    }

    async function providerCancelSeekingActiveProvision() {
        await doSomething(() => seekingService.providerCancelSeekingActiveProvision(seeking), true);
    }

    function isSyncedWithZiskej(s: Seeking): boolean {
        return !!s.ziskejSync;
    }

    function isIntentAllowed(s: Seeking, intentId: SeekingIntentId) {
        return findFirst(s.intents, byId(intentId)).permission.allowed;
    }
</script>

<KpCollapsibleMenuWrapper>
    <div slot="main" class="list-item-provided-seeking">
        <div class="provided-seeking-main">
            <div class="provided-seeking-requester">
                <Label labeled={seeking.seeker} explicitKind={Kind.KIND_USER}/>
                <span class="text-muted">Žádá o</span>
            </div>

            <div class="provided-seeking-request-exemplar">
                <DesiredExemplar desiredExemplar={seeking.desiredExemplar}/>
            </div>
        </div>

        <div class="provided-seeking-side">
            {#if seeking.cancelled}
                <div>
                    <strong>ZRUŠENO</strong>
                </div>
            {/if}
            <div>
                <strong>{pipe(seeking.activeProvision.state, loc())}</strong>
            </div>
            {#if seeking.activeProvision.providerReferenceId || seeking.ziskejSync}
                <div>
                    {#if seeking.activeProvision.providerReferenceId}
                        <kbd>{seeking.activeProvision.providerReferenceId}</kbd>
                    {/if}
                    {#if seeking.ziskejSync}
                        <span class="label label-warning">Získej</span>
                    {/if}
                </div>
            {/if}
            <div>
                <div>
                    <span class="text-muted">{localize(/* @kp-localization commons.Vytvoreno */ 'commons.Vytvoreno')}</span>
                    <span>{pipe(seeking.createDate, dateFormatter('d.M.yyyy'))}</span>
                </div>
                {#if seeking.commenceDate}
                    <div>
                        <span class="text-muted">{localize(/* @kp-localization ill.CommenceDate */ 'ill.CommenceDate')}</span>
                        <span>{pipe(seeking.commenceDate, dateFormatter('d.M.yyyy'))}</span>
                    </div>
                {/if}
                {#if seeking.activeProvision.providerAcceptDate}
                    <div>
                        <span class="text-muted">Schváleno námi</span>
                        <span>{pipe(seeking.activeProvision.providerAcceptDate, dateFormatter('d.M.yyyy'))}</span>
                    </div>
                    {#if seeking.activeProvision.providerAcceptCondition}
                        <div>
                            <span class="text-muted">S podmínkou: </span>
                            <span>{seeking.activeProvision.providerAcceptCondition}</span>
                        </div>
                    {/if}
                {/if}
                {#if seeking.activeProvision.seekerAcceptDate}
                    <div>
                        <span class="text-muted">Schváleno žadatelem</span>
                        <span>{pipe(seeking.activeProvision.seekerAcceptDate, dateFormatter('d.M.yyyy'))}</span>
                    </div>
                {/if}
                {#if seeking.activeProvision.providerReserveDate}
                    <div>
                        <span class="text-muted">{localize(/* @kp-localization ill.provision.ExemplarReservationDate */ 'ill.provision.ExemplarReservationDate')}</span>
                        <span>{pipe(seeking.activeProvision.providerReserveDate, dateFormatter('d.M.yyyy'))}</span>
                    </div>
                {/if}
                {#if seeking.activeProvision.providerSendDate}
                    <div>
                        <span class="text-muted">{localize(/* @kp-localization ill.provision.ProviderSendDate.abbr */ 'ill.provision.ProviderSendDate.abbr')}</span>
                        <span>{pipe(seeking.activeProvision.providerSendDate, dateFormatter('d.M.yyyy'))}</span>
                    </div>
                {/if}
                {#if seeking.activeProvision.providerReceiveDate}
                    <div>
                        <span class="text-muted">{localize(/* @kp-localization ill.provision.ExemplarReceiveDate */ 'ill.provision.ExemplarReceiveDate')}</span>
                        <span>{pipe(seeking.activeProvision.providerReceiveDate, dateFormatter('d.M.yyyy'))}</span>
                    </div>
                {/if}
            </div>
            {#if seeking.activeProvision.price}
                <div>
                    <KpPrice price={seeking.activeProvision.price}/>
                </div>
            {/if}
        </div>
    </div>
    <div slot="menu" let:collapsed>
        {#if isSyncedWithZiskej(seeking)}
            <li class="collapsible-menu-item">
                <a class="btn btn-block btn-warning" href={seeking.ziskejSync.webUrl} target="_blank" role="button">
                    <span class:sr-only={!collapsed} aria-hidden="true"><strong>Z</strong></span>
                    <span class:sr-only={collapsed}>Zobrazit na Získej</span>
                </a>
            </li>
        {/if}
        {#if isIntentAllowed(seeking, SeekingIntentId.seekingActiveProvisionExemplarReceiveBack)}
            <li class="collapsible-menu-item">
                <button class="btn btn-block btn-default" disabled={processing} on:click={() => receiveBackIllSeekingActiveProvisionExemplar()}>
                    <span class="glyphicon glyphicon-download-alt" aria-hidden="true"></span>
                    <span class:sr-only={collapsed}>Přijmout zpět</span>
                </button>
            </li>
        {/if}
        {#if isIntentAllowed(seeking, SeekingIntentId.seekingActiveProvisionExemplarSend)}
            <li class="collapsible-menu-item">
                <button class="btn btn-block btn-default" disabled={processing} on:click={() => sendIllSeekingActiveProvisionExemplar()}>
                    <span class="glyphicon glyphicon-share-alt" aria-hidden="true"></span>
                    <span class:sr-only={collapsed}>Poslat protistraně</span>
                </button>
            </li>
        {/if}
        {#if isIntentAllowed(seeking, SeekingIntentId.seekingActiveProvisionAccept)}
            <li class="collapsible-menu-item">
                <button class="btn btn-block btn-default" disabled={processing} on:click={() => acceptIllSeekingActiveProvision()}>
                    <span class="glyphicon glyphicon-thumbs-up" aria-hidden="true"></span>
                    <span class:sr-only={collapsed}>Přijmout</span>
                </button>
            </li>
        {/if}
        {#if isIntentAllowed(seeking, SeekingIntentId.seekingActiveProvisionConditionallyAccept)}
            <li class="collapsible-menu-item">
                <button class="btn btn-block btn-default" disabled={processing} on:click={() => conditionallyAcceptIllSeekingActiveProvision()}>
                    <span class="glyphicon glyphicon-thumbs-up" aria-hidden="true"></span>
                    <span class:sr-only={collapsed}>Přijmout s podmínkou</span>
                </button>
            </li>
        {/if}
        {#if isIntentAllowed(seeking, SeekingIntentId.seekingProvidedActiveProvisionDataEdit)}
            <li class="collapsible-menu-item">
                <button class="btn btn-block btn-xs btn-default" disabled={processing} on:click={() => editProvidedSeekingActiveProvisionData()}>
                    <span class="glyphicon glyphicon-pencil" aria-hidden="true"></span>
                    <span class:sr-only={collapsed}>Upravit</span>
                </button>
            </li>
        {/if}
        {#if isIntentAllowed(seeking, SeekingIntentId.seekingActiveProvisionProviderCancel)}
            <li class="collapsible-menu-item">
                <button class="btn btn-block btn-xs btn-default" disabled={processing} on:click={() => providerCancelSeekingActiveProvision()}>
                    <span class="glyphicon glyphicon-remove" aria-hidden="true"></span>
                    <span class:sr-only={collapsed}>Zrušit</span>
                </button>
            </li>
        {/if}
    </div>
</KpCollapsibleMenuWrapper>

<style lang="less">
    :global {
        .menu-wrapper-main:has(.list-item-provided-seeking) {
            background-color: #f8f8f8;
        }
    }

    .list-item-provided-seeking {
        display: grid;
        grid-template-columns: 2fr 1fr;
        gap: 10px;
    }

    .provided-seeking-requester {
        margin-bottom: 4px;
    }

    .provided-seeking-request-exemplar {
        margin-top: 6px;
    }
</style>